// Admin → Data's status as the cards read it (GET /api/dataset/status and the
// login routes' answers): null-safe formatting that never hands an object to
// JSX, the session rows, and the saved backfill / Lion Auto login ids. Every
// server field may be absent or oddly typed. Pure; tested in
// datasetStatus.test.js.

/** A finite number, or null (numeric strings count; '' and junk do not) */
export const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** A rounded, grouped whole number ("1,235"), or an em dash */
export const fmtInt = (v) => (num(v) == null ? '—' : Math.round(num(v)).toLocaleString('en-US'));

/** Any server value as text (an error's message, JSON otherwise), cut at `max` with an ellipsis; '' for none */
export function text(v, max = 300) {
  if (v == null || v === '') return '';
  let s;
  if (typeof v === 'string') s = v;
  else if (typeof v === 'number' || typeof v === 'boolean') s = String(v);
  else if (typeof v === 'object' && typeof v.message === 'string') s = v.message;
  else { try { s = JSON.stringify(v); } catch { s = String(v); } }
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** A failed call's server error text, else the fallback */
export const errorMessage = (err, fallback) => text(err?.response?.data?.error) || fallback;

/** The status's Skybox session rows (those with a whole user id) */
export const statusSessions = (status) => (Array.isArray(status?.sessions) ? status.sessions.filter(s => s && Number.isInteger(s.userId)) : []);

/** A session row's name: display name, else username, else "user N" */
export const sessionName = (s, id) => text(s?.displayName) || text(s?.username) || `user ${id ?? s?.userId}`;

/** The saved backfill logins: dedicatedUserIds, or an older server's one dedicatedUserId */
export function backfillIds(status) {
  if (Array.isArray(status?.dedicatedUserIds)) return status.dedicatedUserIds.filter(Number.isInteger);
  return Number.isInteger(status?.dedicatedUserId) ? [status.dedicatedUserId] : [];
}

/** The saved Lion Auto logins: autoUserIds ([] without it: a server before several logins names none) */
export const autoIds = (status) => (Array.isArray(status?.autoUserIds) ? status.autoUserIds.filter(Number.isInteger) : []);

/** Whether two id lists hold the same ids, whatever their order (junk reads as none) */
export function sameIdSet(a, b) {
  const A = new Set(Array.isArray(a) ? a : []);
  const B = new Set(Array.isArray(b) ? b : []);
  return A.size === B.size && [...A].every(id => B.has(id));
}
